import React, { useState, useRef, useEffect } from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import * as ApiUtils from 'js/utils/api_utils';
import { News } from 'js/social/shared/types';
import NewsComponent from 'js/social/News/News';
import useUsers from 'js/social/Feed/hooks/useUsers';
import { FeedContext } from 'js/social/contexts/feed';

const CompactSearchBar = styled.div`
  max-width: 700px;
  margin: 32px auto 0 auto;
  padding: 0;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px;
  font-size: 16px;
  border-radius: 6px;
  border: 1px solid #ccc;
  outline: none;
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 6px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const AnimatedPanel = styled.div`
  width: 100%;
  max-width: 700px;
  margin: 16px auto 0 auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e5e5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s;
  opacity: 1;
  max-height: 850px;
  &.collapsed {
    opacity: 0;
    max-height: 0;
    pointer-events: none;
  }
`;

const StickyTop = styled.div`
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
  padding: 24px 24px 12px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const StickyBottom = styled.div`
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 2;
  padding: 12px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
`;

const ScrollableResults = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 24px;
  background: #f8f9fa;
`;

const ResultsHeader = styled.h4`
  margin: 16px 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #6c757d;
  background-color: white;
  color: #6c757d;
  cursor: pointer;
  &:hover {
    background-color: #6c757d;
    color: white;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: #6c757d;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const NoResultsContainer = styled.div`
  text-align: center;
  padding: 24px;
  color: #6c757d;
`;

interface JJHomePageSearchProps {
  className?: string;
}

const SearchBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}> = ({ searchQuery, setSearchQuery, isLoading, onSubmit }) => (
  <SearchForm onSubmit={onSubmit}>
    <SearchInput
      type="text"
      placeholder="JJ Homepage input placeholder"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      disabled={isLoading}
    />
    <SubmitButton type="submit" disabled={isLoading || !searchQuery.trim()}>
      {isLoading ? 'Searching...' : 'Search'}
    </SubmitButton>
  </SearchForm>
);

export const JJHomePageSearch: React.FC<JJHomePageSearchProps> = ({
  className,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<News[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const userActions = useUsers();
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Animate panel open/close
  useEffect(() => {
    if (hasSearched && (results.length > 0 || isLoading)) {
      setPanelOpen(true);
    } else {
      setPanelOpen(false);
    }
  }, [hasSearched, results, isLoading]);

  // Scroll to top of results when new search is performed
  useEffect(() => {
    if (hasSearched && scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [results, hasSearched]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const data = await ApiUtils.post(
        routes.api_v0_community_jj_home_page_search_path({
          subdomain_param: 'api',
        }),
        { query: searchQuery.trim() },
      );
      setResults(data.posts || []);
    } catch (error) {
      console.error('Error searching for recommendations:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setResults([]);
    setHasSearched(false);
    setPanelOpen(false);
  };

  return (
    <>
      {!panelOpen && (
        <CompactSearchBar className={className}>
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isLoading={isLoading}
            onSubmit={handleSubmit}
          />
        </CompactSearchBar>
      )}
      <AnimatedPanel className={panelOpen ? '' : 'collapsed'}>
        {panelOpen && (
          <>
            <StickyTop>
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isLoading={isLoading}
                onSubmit={handleSubmit}
              />
              {isLoading && (
                <LoadingContainer>
                  <Spinner />
                  <span>Searching for JJ Home Page Results...</span>
                </LoadingContainer>
              )}
            </StickyTop>
            <ScrollableResults ref={scrollableRef}>
              {results.length > 0 ? (
                <>
                  <ResultsHeader>
                    JJ Home Page Results ({results.length})
                  </ResultsHeader>
                  <FeedContext.Provider
                    value={{
                      useReporting: false,
                      recordViews: false,
                      feedConfig: {
                        page: 'home',
                        openalexRecsTestBucket: '',
                        extendedOnsiteRecsTestBucket: '',
                        recencyWeightedTestBucket: '',
                        guessTheResearcherTestBucket: '',
                        showJJHomePage: true,
                        recommendationRatingsEnabled: false,
                        setFeedConfig: () => {},
                      },
                      userActions,
                      newsActions: {
                        news: results,
                        enableInfiniteScroll: false,
                        isLoading: false,
                        hasLoadedNews: true,
                        hasNextPage: false,
                        newsFeedLoadedRef: { current: Promise.resolve(true) },
                        hasError: false,
                        noNewsFound: false,
                        fetchNextPage: () => null,
                        refreshFeed: async () => null,
                        removeUserContent: () => {},
                        addFeedNews: () => {},
                        removeFeedNews: () => {},
                        updateFeedNews: () => {},
                        likeFeedNews: () => {},
                        unlikeFeedNews: () => {},
                        repostFeedNews: () => {},
                        unrepostFeedNews: () => {},
                        updateWorkBookmarkStatus: () => {},
                        addFeedComment: () => {},
                        removeFeedComment: () => {},
                        updateFeedComment: () => {},
                        likeFeedComment: () => {},
                        unlikeFeedComment: () => {},
                        checkRepeatNews: () => {},
                        checkRepeatPage: () => {},
                      },
                      showInlineReader: false,
                    }}
                  >
                    {results.map((news, index) => (
                      <NewsComponent
                        key={`${news.newsType}-${news.newsId}`}
                        {...news}
                        index={index}
                        getUserData={userActions.getUserData}
                        setUserFollowing={userActions.setUserFollowing}
                        removeUserContent={() => {}}
                        addFeedNews={() => {}}
                        removeFeedNews={() => {}}
                        updateFeedNews={() => {}}
                        likeFeedNews={() => {}}
                        unlikeFeedNews={() => {}}
                        repostFeedNews={() => {}}
                        unrepostFeedNews={() => {}}
                        updateWorkBookmarkStatus={() => {}}
                        addFeedComment={() => {}}
                        removeFeedComment={() => {}}
                        updateFeedComment={() => {}}
                        likeFeedComment={() => {}}
                        unlikeFeedComment={() => {}}
                      />
                    ))}
                  </FeedContext.Provider>
                </>
              ) : (
                !isLoading && (
                  <NoResultsContainer>
                    <div>No JJ Home Page Results found for "{searchQuery}"</div>
                  </NoResultsContainer>
                )
              )}
            </ScrollableResults>
            <StickyBottom>
              <ClearButton onClick={handleClear}>Clear Search</ClearButton>
            </StickyBottom>
          </>
        )}
      </AnimatedPanel>
    </>
  );
};
